import React, { useEffect, useState } from 'react';
import { employeeWalletAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, ChevronDown, ChevronUp, FileText, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';

const fmt = (n) => `₦${Math.round(Number(n || 0)).toLocaleString()}`;

export default function EmployeePay() {
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaye, setShowPaye] = useState(false);

  useEffect(() => {
    const load = async () => {
      try { setComp(await employeeWalletAPI.getCompensation()); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;
  if (!comp) return null;

  const benefitTotal = comp.total_benefits || 0;
  const lines = [
    { label: 'Your bank account', amount: comp.cash_to_bank, color: '#00B894', bold: true, icon: '💰' },
    { label: 'Pension (company contribution)', amount: comp.employer_pension, color: '#6366f1', icon: '🏦' },
    { label: 'Pension (your contribution)', amount: comp.employee_pension, color: '#6366f1', icon: '🏦' },
    ...(comp.benefits ? Object.entries(comp.benefits).map(([k, v]) => ({
      label: `${k.charAt(0).toUpperCase() + k.slice(1)} benefit`,
      amount: v,
      color: { food: '#f59e0b', transport: '#ec4899', health: '#00B894', insurance: '#8b5cf6' }[k] || '#9ca3af',
      icon: { food: '🍽️', transport: '🚗', health: '🏥', insurance: '🛡️' }[k] || '🎁',
    })) : []),
    { label: 'PAYE tax', amount: comp.paye_tax, color: '#6b7280', icon: '🏛️' },
  ];

  const now = new Date();
  const monthYear = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-5 pb-6" data-testid="employee-pay">
      <h1 className="text-xl font-bold text-gray-900">My Compensation — {monthYear}</h1>
      <p className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(comp.total_package)} <span className="text-sm font-normal text-gray-400">Total Monthly Package</span></p>

      {/* Flow Breakdown */}
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-5">
          <div className="space-y-2.5">
            {lines.map((item, i) => (
              <div key={i}>
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2.5">
                    <span className="text-sm">{item.icon}</span>
                    <span className={`text-sm ${item.bold ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{item.label}</span>
                  </div>
                  <span className={`text-sm font-bold ${item.bold ? 'text-emerald-600' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono' }}>{fmt(item.amount)}</span>
                </div>
                {(i === 0 || i === 2 || i === 2 + Object.keys(comp.benefits || {}).length) && <hr className="border-gray-100 my-1" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* PAYE Drill-down */}
      <button onClick={() => setShowPaye(!showPaye)} className="flex items-center gap-2 text-sm text-pink-600 font-medium hover:text-pink-700">
        {showPaye ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        How is my PAYE calculated?
      </button>

      {showPaye && (
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-4 text-xs space-y-2">
            <div className="flex justify-between"><span className="text-gray-600">Annual Gross Salary:</span><span className="font-bold">{fmt(comp.annual_gross)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">- Employee Pension (8%):</span><span className="text-red-500">-{fmt(comp.employee_pension * 12)}</span></div>
            {comp.benefits?.insurance > 0 && <div className="flex justify-between"><span className="text-gray-600">- Life Insurance Relief:</span><span className="text-red-500">-{fmt(comp.benefits.insurance * 12)}</span></div>}
            <div className="flex justify-between border-t pt-2"><span className="font-semibold text-gray-900">Chargeable Income:</span><span className="font-bold">{fmt(comp.chargeable_income)}</span></div>
            <div className="mt-3 pt-2 border-t">
              <p className="font-semibold text-gray-700 mb-1">Tax Bands (NTA 2025):</p>
              {[
                { range: 'First ₦800,000', rate: '0%' },
                { range: 'Next ₦2,200,000', rate: '15%' },
                { range: 'Next ₦9,000,000', rate: '18%' },
                { range: 'Next ₦13,000,000', rate: '21%' },
                { range: 'Next ₦25,000,000', rate: '23%' },
                { range: 'Above ₦50,000,000', rate: '25%' },
              ].map((b, i) => (
                <div key={i} className="flex justify-between py-0.5">
                  <span className="text-gray-500">{b.range}</span>
                  <span className="text-gray-700">@ {b.rate}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between border-t pt-2"><span className="text-gray-600">Annual PAYE:</span><span className="font-bold">{fmt(comp.annual_paye)}</span></div>
            <div className="flex justify-between"><span className="font-semibold text-gray-900">Monthly PAYE:</span><span className="font-bold text-pink-600">{fmt(comp.paye_tax)}</span></div>
          </CardContent>
        </Card>
      )}

      {/* Benefits Note */}
      <Card className="border-emerald-100 bg-emerald-50">
        <CardContent className="p-4">
          <p className="text-xs text-emerald-700">Your company provides <strong>{fmt(benefitTotal)}</strong> in monthly benefits through approved vendors — on top of your take-home pay.</p>
        </CardContent>
      </Card>

      {/* Payslip History */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Payslip History</h2>
        <Card className="border-gray-200">
          <CardContent className="p-0 divide-y divide-gray-100">
            {['February 2026', 'January 2026'].map((m, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-900">{m}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-900" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(comp.cash_to_bank)}</span>
                  <Button size="sm" variant="ghost" className="text-xs text-pink-500 h-7 px-2"><Download className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
